/**
 * File-attachment limits + validation, shared by every AI chat surface
 * (btgpt, the agent builder). Same values on the backend (global-constraints).
 * The actual upload is injected by the caller (`AiChatAttachmentsOptions.upload`) —
 * this module never talks to the network.
 */

export const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;
export const MAX_ATTACHMENTS_PER_MESSAGE = 10;
/** Fix round 1 (Task 7) — the builder's "empty prompt allowed only with
 *  attachments" rule needs SOME user intent text to reason over; the backend
 *  substitutes this same string when btgpt is sent an empty message +
 *  attachments. Kept here so every caller uses the identical literal. */
export const DEFAULT_ATTACHMENT_PROMPT = 'See the attached file(s).';
export const ACCEPT_ATTR = 'image/jpeg,image/png,image/webp,image/gif,application/pdf,.docx,.xlsx,.csv,.txt,.md';

const ALLOWED_MIME_TYPES: ReadonlySet<string> = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv',
  'text/plain',
  'text/markdown',
]);

/** When the browser sends an empty MIME (common for `.md`/`.csv` on some OSes), fall back by extension. */
const EXTENSION_MIME_FALLBACK: Readonly<Record<string, string>> = {
  '.md': 'text/markdown',
  '.csv': 'text/csv',
  '.txt': 'text/plain',
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

/**
 * MIME aliases browsers/OSes report for CSV/Markdown that are NOT in
 * `ALLOWED_MIME_TYPES`. Chrome/Firefox on Windows report `.csv` as
 * `application/vnd.ms-excel` whenever Excel/Office is installed — that is
 * non-empty, so the plain empty-MIME extension fallback never ran and a
 * `.csv` picked via the file dialog was rejected. Treated as "generic":
 * decide purely by extension, same as an empty MIME. Must mirror the
 * backend's `GENERIC_MIME_ALIASES` (limits.ts) exactly.
 */
const GENERIC_MIME_ALIASES: ReadonlySet<string> = new Set([
  'application/vnd.ms-excel',
  'text/x-csv',
  'application/csv',
  'text/comma-separated-values',
  'text/x-markdown',
]);

function extensionOf(name: string): string {
  const i = name.lastIndexOf('.');
  return i === -1 ? '' : name.slice(i).toLowerCase();
}

/** The MIME a file resolves to for validation: its own `type`, or an extension fallback when that is empty or a known generic alias. */
function resolvedMime(file: File): string {
  const type = (file.type || '').toLowerCase();
  if (type && !GENERIC_MIME_ALIASES.has(type)) return type;
  return EXTENSION_MIME_FALLBACK[extensionOf(file.name)] ?? '';
}

/** `null` = ok to upload; otherwise a short user-facing reason. */
export function validateAttachment(file: File): string | null {
  if (file.size > MAX_ATTACHMENT_BYTES) return 'too large (max 25 MB)';
  const mime = resolvedMime(file);
  if (!mime || !ALLOWED_MIME_TYPES.has(mime)) return 'type not supported';
  return null;
}

/** What an upload resolves to: the client holds only the id, never a signed URL. */
export interface UploadedAttachment {
  uploadId: string;
  name: string;
  mime: string;
  size: number;
}

export interface AiChatAttachmentsOptions {
  /** Starts immediately when a file is added. Rejecting marks that file's chip as an error. */
  upload: (file: File) => Promise<UploadedAttachment>;
}
